import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { Calendar, Filter, PlusCircle } from "feather-icons-react/build/IconComponents";
import { ErrorToaster } from "../components/toaster/Toaster";
import SaleInvoiceServices from "../../services/SaleInvoice";
import { deleteAlert } from "../components/sweetAlert";
import { cleanObject, DateFormat, Debounce, getDate } from "../../utils";
import { all_routes } from "../../Router/all_routes";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Controller, useForm } from "react-hook-form";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import InputField from "../components/Input";
import StoreServices from "../../services/Store";
import DeliveryTypeServices from "../../services/DeliveryType";
import IMask from "imask";
import { statuses } from "../../core/json/statuses";
import RefundExchangeForm from '../refund-exchange/forms/RefundExchangeForm';

const Sales = () => {

  const routes = all_routes;
  const [searchParams, setSearchParams] = useSearchParams();

  const { register, handleSubmit, control, setValue } = useForm();

  // *For Loader
  const [loader, setLoader] = useState(false);

  // *For Store
  const [stores, setStores] = useState([]);

  // *For Delivery Type
  const [deliveryTypes, setDeliveryTypes] = useState([]);

  // *For Sale Invoices
  const [saleInvoices, setSaleInvoices] = useState([]);

  // *For Pagination
  const [totalCount, setTotalCount] = useState(0);
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const pageLimit = parseInt(searchParams.get("limit") || "10", 10);
  const searchText = searchParams.get("search") || "";

  // *For Filter
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  // *For Get Store
  const getStores = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await StoreServices.getStores(params)
      setStores(data.map((e) => { return { label: e?.name, value: e?.id } }))
    } catch (error) {
      console.log('getStores => error:', error)
    }
  }

  // *For Get Delivery Type 
  const getDeliveryTypes = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await DeliveryTypeServices.getDeliveryTypes(params)
      setDeliveryTypes(data.map((e) => { return { label: e?.name, value: e?.id } }))
    } catch (error) {
      console.log('getDeliveryTypes => error:', error)
    }
  }

  // *For Get Sale Invoices
  const getSaleInvoices = async (page = currentPage, limit = pageLimit) => {
    setLoader(true)
    try {
      const params = { ...Object.fromEntries(searchParams.entries()), page, limit }
      const { data, total_count } = await SaleInvoiceServices.getSaleInvoices(params)
      setSaleInvoices(data)
      setTotalCount(total_count)
    } catch (error) {
      ErrorToaster(error)
    } finally {
      setLoader(false)
    }
  }

  // *For Delete Sale Invoice
  const deleteSaleInvoice = async (id) => {
    try {
      const isConfirmed = await deleteAlert()

      if (isConfirmed) {
        await SaleInvoiceServices.deleteSaleInvoice(id);
        getSaleInvoices();

        Swal.fire({
          title: "Deleted!",
          text: "Sale invoice has been deleted.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        Swal.close();
      }
    } catch (error) {
      ErrorToaster(error)
    }
  }

  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundInvoice, setRefundInvoice] = useState(null);

  const columns = [
    {
      title: "Purchase Date",
      dataIndex: "order_date",
      render: (text) => (
        <span>
          {getDate(text)}
        </span>
      ),
    },
    {
      title: "Order #",
      dataIndex: "order_number",
    },
    {
      title: "Customer Name",
      dataIndex: "billing_name",
      render: (text, record) => (
        <span>
          {text} {record?.billing_last_name}
        </span>
      ),
    },
    {
      title: "Delivery Date",
      dataIndex: "delivery_date",
      render: (text) => (
        <span>
          {getDate(text)}
        </span>
      ),
    },
    {
      title: "Delivery Type",
      dataIndex: "delivery_type",
      render: (text) => (
        <span>
          {text?.name}
        </span>
      ),
    },
    {
      title: "Grand Total",
      dataIndex: "total_sale",
      render: (text, record) => (
        <span>
          ${parseFloat(record?.total_sale).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Paid",
      dataIndex: "paid",
      render: (text, record) => {
        const paidOrder = parseFloat(record?.total_sale) - parseFloat(record?.balance_amount)
        return (
          <span>
            ${paidOrder.toFixed(2)}
          </span>
        )
      },
    },
    {
      title: "Due",
      dataIndex: "balance_amount",
      render: (text, record) => (
        <span className={record?.balance_amount > 0 ? "text-danger" : ""}>
          ${parseFloat(record?.balance_amount).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text, record) => {
        // const paidOrder = (parseFloat(record?.total_sale) - parseFloat(record?.balance_amount)).toFixed(2)
        // const status = paidOrder == 0 ? "Unpaid" : paidOrder == parseFloat(record?.total_sale).toFixed(2) ? "Paid" : "Partial"
        // const statusColor = status == "Unpaid" ? "badges-danger" : status == "Paid" ? "badges-success" : "badges-warning"
        // const status = paidOrder == parseFloat(record?.total_sale).toFixed(2) ? "Paid" : "Balance Due"
        // const statusColor = status == "Paid" ? "badge-linesuccess" : "badge-linedanger"
        const statusColor = text !== "PARTIALLY_PAID" ? "badge-linesuccess" : "badge-linedanger"
        return (
          <span
            className={`badge ${statusColor}`}
          >
            {text}
          </span>
        )
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (text, record) => (
        <div className="text-center">
          <Link className="action-set" to="#" data-bs-toggle="dropdown" aria-expanded="true">
            <i className="fa fa-ellipsis-v" aria-hidden="true"></i>
          </Link>
          <ul className="dropdown-menu">
            <li>
              <Link to={`${routes?.viewSaleInvoice}/${record?.id}`} className="dropdown-item"><i data-feather="eye" className="feather-eye me-2"></i>View Invoice</Link>
            </li>
            <li>
              <Link to={`${routes?.viewOriginalSaleInvoice}/${record?.id}`} className="dropdown-item"><i data-feather="eye" className="feather-eye me-2"></i>Original Invoice</Link>
            </li>
            <li>
              <Link to={`${routes?.refundExchange}/${record?.id}`} className="dropdown-item"><i className="fas fa-undo-alt me-2"></i>Refund & Exchange</Link>
            </li>
            {!['SHIPPIED', 'LOCKED'].includes(record?.status) &&
              <li>
                <Link to={`${routes?.editSale}/${record?.id}`} className="dropdown-item"><i data-feather="edit" className="feather-edit me-2"></i>Edit Sale</Link>
              </li>
            }
            {/* <li>
              <Link to="#" className="dropdown-item" data-bs-toggle="modal" data-bs-target="#status-modal"><i data-feather="check-circle" className="feather-check-circle me-2"></i>Status</Link>
            </li> */}
            {/* <li>
              <Link to="#" className="dropdown-item" data-bs-toggle="modal" data-bs-target="#createpayment"><i data-feather="plus-circle" className="feather-plus-circle me-2"></i>Create Payment</Link>
            </li> */}
            {/* <li>
              <Link to="#" className="dropdown-item"><i data-feather="download" className="feather-edit me-2"></i>Download pdf</Link>
            </li> */}
            <li>
              <Link to="#" className="dropdown-item confirm-text mb-0" onClick={() => deleteSaleInvoice(record?.id)}><i data-feather="trash-2" className="feather-trash me-2"  ></i>Delete Sale</Link>
            </li>
          </ul>
        </div>
      ),
    },
  ];

  // *For Handle Pagination and Filters
  const handlePaginationFilter = (data) => {
    try {
      const obj = {
        ...data,
        orderDate: data?.orderDate ? dayjs(data?.orderDate).format('YYYY-MM-DD') : null,
        deliveryDate: data?.deliveryDate ? dayjs(data?.deliveryDate).format('YYYY-MM-DD') : null,
      }
      const updatedParams = {
        ...Object.fromEntries(searchParams.entries()),
        page: 1,
        ...cleanObject(obj)
      };
      Debounce(() => setSearchParams(updatedParams));
    } catch (error) {
      console.log(' handlePaginationFilter => error:', error)
    }
  }

  useEffect(() => {
    getSaleInvoices()
    if (searchParams) {
      setValue('orderNumber', searchParams.get('orderNumber') || '')
    }
  }, [searchParams]);

  useEffect(() => {
    getStores()
    getDeliveryTypes()
  }, []);

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Sale Invoice</h4>
                <h6>Manage Your Sale Invoices</h6>
              </div>
            </div>
            <div className="page-btn">
              <Link
                to={routes?.createSale}
                className="btn btn-added"
              >
                <PlusCircle className="me-2" />
                Add New Sale Invoice
              </Link>
            </div>
          </div>
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top">
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Search by customer name"
                      defaultValue={searchText}
                      className="form-control form-control-sm formsearch"
                      onChange={(e) => handlePaginationFilter({ search: e.target.value })}
                    />
                    <Link to className="btn btn-searchset">
                      <i data-feather="search" className="feather-search" />
                    </Link>
                  </div>
                </div>
                <div className="search-path">
                  <div
                    className={`btn btn-filter cursor-pointer ${isFilterVisible ? "setclose" : ""}`}
                    id="filter_search"
                    onClick={() => setIsFilterVisible((prev) => !prev)}
                  >
                    <Filter
                      className="filter-icon"
                    />
                    <span>
                      <ImageWithBasePath
                        src="assets/img/icons/closes.svg"
                        alt="img"
                      />
                    </span>
                  </div>
                </div>
              </div>
              {isFilterVisible &&
                <div className={`border-0 card${isFilterVisible ? " visible" : ""}`} >
                  <div className="card-body pb-0 px-4">
                    <form onSubmit={handleSubmit(handlePaginationFilter)}>
                      <div className="row align-items-center">
                        <div className="col-lg-3 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <InputField
                              label="Order#"
                              placeholder='Order Number'
                              type="text"
                              register={register("orderNumber")}
                            />
                          </div>
                        </div>
                        <div className="col-lg-3 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <Controller
                              name="phone"
                              control={control}
                              rules={{
                                pattern: {
                                  value: /^\(\d{3}\) \d{3}-\d{4}$/,
                                  message: "Invalid phone number format"
                                }
                              }}
                              render={({ field }) => {
                                const inputRef = useRef(null);
                                useEffect(() => {
                                  if (inputRef.current) {
                                    const mask = IMask(inputRef.current, {
                                      mask: '(000) 000-0000'
                                    });
                                    return () => mask.destroy();
                                  }
                                }, []);
                                return (
                                  <>
                                    <label className="form-label">Phone</label>
                                    <input
                                      {...field}
                                      ref={(e) => {
                                        field.ref(e);
                                        inputRef.current = e;
                                      }}
                                      className={`form-control`}
                                      style={{ height: '40px' }}
                                      placeholder="(123) 456-7890"
                                    />
                                  </>
                                );
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-lg-3 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <label className="form-label">Delivery Type</label>
                            <Controller
                              control={control}
                              name={'deliveryType'}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  size="large"
                                  style={{ width: '100%' }}
                                  options={deliveryTypes}
                                  onChange={(value) => field.onChange(value)}
                                />
                              )}
                            />
                          </div>
                        </div>
                        {/* <div className="col-lg-3 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <label className="form-label">Order Status</label>
                            <Controller
                              control={control}
                              name={'orderStatus'}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  size="large"
                                  style={{ width: '100%' }}
                                  options={[{ value: "pending", label: "Pending" }, { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" }]}
                                  onChange={(value) => field.onChange(value)}
                                />
                              )}
                            />
                          </div>
                        </div> */}
                        <div className="col-lg-3 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <div className="input-blocks mb-0">
                              <label className="form-label">Order Date</label>
                              <div className="input-groupicon calender-input">
                                <Calendar className="info-img" />
                                <Controller
                                  name="orderDate"
                                  control={control}
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker
                                      className="datetimepicker"
                                      placeholder="Order Date"
                                      size="small"
                                      format={DateFormat}
                                      onChange={(date) => onChange(date || null)}
                                      onBlur={onBlur}
                                      value={value ? dayjs(value, DateFormat) : null}
                                      ref={ref}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-3 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <div className="input-blocks mb-0">
                              <label className="form-label">Delivery Date</label>
                              <div className="input-groupicon calender-input">
                                <Calendar className="info-img" />
                                <Controller
                                  name="deliveryDate"
                                  control={control}
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker
                                      className="datetimepicker"
                                      placeholder="Delivery Date"
                                      size="small"
                                      format={DateFormat}
                                      onChange={(date) => onChange(date || null)}
                                      onBlur={onBlur}
                                      value={value ? dayjs(value, DateFormat) : null}
                                      ref={ref}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-3 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <label className="form-label">Store</label>
                            <Controller
                              control={control}
                              name={'store'}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  size="large"
                                  style={{ width: '100%' }}
                                  options={stores}
                                  onChange={(value) => field.onChange(value)}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="col-lg-3 col-sm-6 col-12">
                          <div className="mb-1 add-product">
                            <label className="form-label">Status</label>
                            <Controller
                              control={control}
                              name={'status'}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  size="large"
                                  style={{ width: '100%' }}
                                  options={statuses}
                                  onChange={(value) => field.onChange(value)}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="col-lg-3 col-sm-6 col-12 ms-auto">
                          <div className="input-blocks mb-1">
                            <button type="submit" className="btn btn-filters ms-auto">
                              <i
                                data-feather="search"
                                className="feather-search"
                              />
                              Search
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              }

              <div className="table-responsive">
                <Table
                  loading={loader}
                  columns={columns}
                  dataSource={saleInvoices}
                  totalCount={totalCount}
                  pageSize={pageLimit}
                  currentPage={currentPage}
                  onChange={(page, pageSize) => handlePaginationFilter({ page: page, limit: pageSize })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Refund/Exchange Modal */}
      {showRefundModal && refundInvoice && (
        <RefundExchangeForm
          preselectedInvoices={[refundInvoice]}
          preselectedItems={refundInvoice.sale_invoice_items?.map(item => ({ ...item, invoiceId: refundInvoice.id }))}
          onSubmit={() => { setShowRefundModal(false); setRefundInvoice(null); }}
          onClose={() => { setShowRefundModal(false); setRefundInvoice(null); }}
        />
      )}
    </div>
  );
};

export default Sales;
